import {
  formatExecutionType,
  formatExpirationFlags,
  formatFillingFlags,
  formatSwapType,
  formatTradeMode,
  formatV3DaysSwap,
  type CommissionTier,
  type SymbolBaseInfo,
  type SymbolCommission,
  type SymbolGroupTemplate,
} from "./types";
import { getDayLabel } from "./symbolSessions";

export type SymbolDetailItem = {
  label: string;
  value: string;
};

const EMPTY = "—";

const formatEnumValue = (
  value: number | undefined,
  labels: Record<number, string>
): string => {
  if (value === undefined || !Number.isFinite(value)) return EMPTY;
  return `${value} - ${labels[value] ?? "Unknown"}`;
};

const formatFlagValue = (
  value: number | undefined,
  labels: Array<[number, string]>
): string => {
  if (value === undefined || !Number.isFinite(value)) return EMPTY;
  if (value === 0) return "0 - None";

  const names = labels
    .filter(([flag]) => (value & flag) === flag)
    .map(([, label]) => label);

  return `${value} - ${names.length > 0 ? names.join(", ") : "Unknown"}`;
};

const formatNum = (n: number | undefined, digits = 6) =>
  n !== undefined && Number.isFinite(n) ? n.toFixed(digits) : EMPTY;

const formatText = (value: string | undefined | null) =>
  value && value.trim() ? value : EMPTY;

const commissionTypeLabels: Record<number, string> = {
  0: "Default",
  1: "Turn money",
  2: "Turn lots",
  3: "Deals",
  4: "Profit",
};

const commissionPeriodLabels: Record<number, string> = {
  0: "Daily",
  1: "Monthly",
  2: "Instant",
};

const instantCommissionTypeLabels: Record<number, string> = {
  0: "In/out deals",
  1: "In deals",
  2: "Out deals",
};

const commissionUnitTypeLabels: Record<number, string> = {
  0: "Currency",
  1: "Base currency",
  2: "Profit currency",
  3: "Margin currency",
  4: "Points",
  5: "Money currency",
  6: "Specified",
  7: "Profit",
};

const commissionUnitModeLabels: Record<number, string> = {
  0: "Per deal",
  1: "Per lot",
  2: "Per value",
};

const orderFlagLabels: Array<[number, string]> = [
  [1, "Market"],
  [2, "Limit"],
  [4, "Stop"],
  [8, "Stop limit"],
  [16, "SL"],
  [32, "TP"],
  [64, "Close by"],
];

const marginFlagLabels: Array<[number, string]> = [
  [4, "Hedged margin calculation"],
];

const profitCalcFlagLabels: Array<[number, string]> = [
  [1, "Forex profit calc mode"],
];

const marginRateLabels = [
  "Buy",
  "Sell",
  "Buy limit",
  "Sell limit",
  "Buy stop",
  "Sell stop",
  "Buy stop limit",
  "Sell stop limit",
] as const;

const swapRateDayIndices = [0, 1, 2, 3, 4, 5, 6];

export const formatCommissionType = (value: number | undefined): string =>
  formatEnumValue(value, commissionTypeLabels);

export const formatCommissionPeriod = (value: number | undefined): string =>
  formatEnumValue(value, commissionPeriodLabels);

export const formatInstantCommissionType = (value: number | undefined): string =>
  formatEnumValue(value, instantCommissionTypeLabels);

export const formatCommissionUnitType = (value: number | undefined): string =>
  formatEnumValue(value, commissionUnitTypeLabels);

export const formatCommissionUnitMode = (value: number | undefined): string =>
  formatEnumValue(value, commissionUnitModeLabels);

export const formatOrderFlags = (value: number | undefined): string =>
  formatFlagValue(value, orderFlagLabels);

export const formatMarginFlags = (value: number | undefined): string =>
  formatFlagValue(value, marginFlagLabels);

export const formatProfitCalcFlags = (value: number | undefined): string =>
  formatFlagValue(value, profitCalcFlagLabels);

export function formatVolumeInternal(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return EMPTY;

  const lots = value / 100_000_000;
  return `${value} (${lots.toFixed(8)} lots)`;
}

export function formatMarginRateRows(
  rates: number[] | undefined,
  prefix: string
): SymbolDetailItem[] {
  if (!rates || rates.length === 0) {
    return [{ label: prefix, value: EMPTY }];
  }

  return marginRateLabels.map((label, index) => ({
    label: `${prefix} — ${label}`,
    value: formatNum(rates[index]),
  }));
}

export function formatSwapRatesByDay(rates: number[] | undefined): string {
  if (!rates || rates.length === 0) return EMPTY;

  return swapRateDayIndices
    .map((dayIndex) => {
      const rate = rates[dayIndex];
      const rateText =
        rate !== undefined && Number.isFinite(rate) ? formatNum(rate) : EMPTY;
      return `${getDayLabel(dayIndex)}: ${rateText}`;
    })
    .join(", ");
}

export function formatSwapRateRows(
  rates: number[] | undefined
): SymbolDetailItem[] {
  if (!rates || rates.length === 0) {
    return [{ label: "Swap rates", value: EMPTY }];
  }

  return swapRateDayIndices.map((dayIndex) => ({
    label: getDayLabel(dayIndex),
    value: formatNum(rates[dayIndex]),
  }));
}

export function getCommissionHeaderRows(
  commission: SymbolCommission
): SymbolDetailItem[] {
  return [
    { label: "Group pattern", value: formatText(commission.groupPattern) },
    { label: "Type", value: formatCommissionType(commission.type) },
    { label: "Period", value: formatCommissionPeriod(commission.period) },
    { label: "Money currency", value: formatText(commission.moneyCurrency) },
    {
      label: "Instant type",
      value: formatInstantCommissionType(commission.instantType),
    },
  ];
}

export function getCommissionTierRows(
  tier: CommissionTier,
  index: number
): SymbolDetailItem[] {
  const prefix = `Tier ${index + 1}`;
  return [
    { label: `${prefix} — Unit type`, value: formatCommissionUnitType(tier.unitType) },
    { label: `${prefix} — Unit mode`, value: formatCommissionUnitMode(tier.unitMode) },
    { label: `${prefix} — Unit value`, value: formatNum(tier.unitValue) },
    { label: `${prefix} — Min value`, value: formatNum(tier.minValue) },
    { label: `${prefix} — Max value`, value: formatNum(tier.maxValue) },
    { label: `${prefix} — Min unit`, value: formatNum(tier.minUnit) },
    { label: `${prefix} — Max unit`, value: formatNum(tier.maxUnit) },
    { label: `${prefix} — Currency unit`, value: formatText(tier.currencyUnit) },
  ];
}

export function getCommissionSectionRows(
  commission: SymbolCommission | null | undefined
): SymbolDetailItem[] {
  if (!commission) {
    return [{ label: "Commission", value: "No commission rule matched" }];
  }

  const rows = getCommissionHeaderRows(commission);
  commission.tiers?.forEach((tier, index) => {
    rows.push(...getCommissionTierRows(tier, index));
  });
  return rows;
}

export function getGroupTemplateRows(
  template: SymbolGroupTemplate
): SymbolDetailItem[] {
  return [
    { label: "Trade mode", value: formatTradeMode(template.tradeMode) },
    { label: "Execution type", value: formatExecutionType(template.tradeType) },
    { label: "Filling flags", value: formatFillingFlags(template.fillPolicy) },
    {
      label: "Expiration flags",
      value: formatExpirationFlags(template.expiration),
    },
    { label: "Deviation rate", value: String(template.deviationRate) },
    { label: "Round rate", value: String(template.roundRate) },
    { label: "Order flags", value: formatOrderFlags(template.orderFlags) },
    { label: "SL level", value: String(template.sl) },
    { label: "TP level", value: String(template.tp) },
    { label: "Price timeout", value: String(template.priceTimeout) },
    { label: "Requote timeout", value: String(template.requoteTimeout) },
    { label: "Request lots", value: String(template.requestLots) },
    { label: "Min lots", value: formatNum(template.minLots, 2) },
    { label: "Min volume", value: formatVolumeInternal(template.minVolume) },
    { label: "Max lots", value: formatNum(template.maxLots, 2) },
    { label: "Max volume", value: formatVolumeInternal(template.maxVolume) },
    { label: "Lots step", value: formatNum(template.lotsStep, 2) },
    { label: "Volume step", value: formatVolumeInternal(template.volumeStep) },
    { label: "Initial margin", value: formatNum(template.initialMargin) },
    {
      label: "Maintenance margin",
      value: formatNum(template.maintenanceMargin),
    },
    { label: "Hedged margin", value: formatNum(template.hedgedMargin) },
    { label: "Margin flags", value: formatMarginFlags(template.marginFlags) },
    {
      label: "Hedged margin rate",
      value: formatNum(template.hedgedMarginRate),
    },
    { label: "Swap type", value: formatSwapType(template.swapType) },
    { label: "Swap long", value: formatNum(template.swapLong) },
    { label: "Swap short", value: formatNum(template.swapShort) },
    {
      label: "Triple swap day",
      value: formatV3DaysSwap(template.v3DaysSwap),
    },
    ...formatMarginRateRows(template.initMarginRate, "Initial margin rate"),
    ...formatMarginRateRows(template.mntnMarginRate, "Maintenance margin rate"),
    ...formatSwapRateRows(template.swapRates),
  ];
}

export function getSymbolBaseInfoRows(
  baseInfo: SymbolBaseInfo | null | undefined
): SymbolDetailItem[] {
  if (!baseInfo) {
    return [{ label: "Base info", value: EMPTY }];
  }

  return [
    { label: "Company", value: formatText(baseInfo.companyName) },
    { label: "Account currency", value: formatText(baseInfo.currency) },
    { label: "Digits", value: String(baseInfo.digits) },
    {
      label: "Symbol set",
      value:
        baseInfo.symbolSet?.length > 0
          ? baseInfo.symbolSet.join(", ")
          : EMPTY,
    },
    { label: "Receive user msg", value: String(baseInfo.receiveUserMsg) },
    { label: "Account method", value: formatText(baseInfo.accountMethod) },
    { label: "Margin mode", value: formatText(baseInfo.marginMode) },
    { label: "Symbols hash", value: formatText(baseInfo.symbolsHash) },
    { label: "Spreads hash", value: formatText(baseInfo.spreadsHash) },
  ];
}

export function formatConfigSpreadNote(
  configSpread: number | undefined,
  liveSpread: number | undefined
): string | undefined {
  if (
    configSpread === undefined ||
    liveSpread === undefined ||
    !Number.isFinite(configSpread) ||
    !Number.isFinite(liveSpread)
  ) {
    return undefined;
  }

  if (configSpread === liveSpread) {
    return "Live spread matches configured spread.";
  }

  return `Configured spread is ${configSpread} points; live spread is ${liveSpread} points.`;
}
